// Shared GST connection utilities — used by both supplier-intelligence and
// lending/underwriting GST flows.

import { getLocalUser } from "../functions/utilities";

/** Standard GSTIN format: 2-digit state code + 10-char PAN + entity code + "Z" + checksum. */
export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

/** Number of digits in a GST portal OTP. */
export const OTP_LENGTH = 6;

/** Default OTP resend countdown in seconds. */
export const OTP_COUNTDOWN_SECONDS = 30;

/**
 * Strip ALL whitespace (not just the ends) from a GSTIN and uppercase it
 * before sending to the backend.
 */
export function cleanGstin(gstin: string): string {
  return gstin.replace(/\s/g, "").toUpperCase();
}

/**
 * Strip ALL whitespace (not just the ends) from a username before sending
 * to the backend.
 */
export function cleanUsername(username: string): string {
  return username.replace(/\s/g, "");
}

/** Returns true if the given GSTIN matches the standard format. */
export function isValidGstin(gstin: string): boolean {
  return GSTIN_REGEX.test(cleanGstin(gstin));
}

// ── Connection progress types ───────────────────────────────────────────────

/** Phases of the GST connection progress animation. */
export type ConnectionPhase =
  | "verifying"
  | "accessing"
  | "fetching"
  | "connected"
  | "failed";

/** A pending GST connection (passed from parent after OTP verification). */
export interface PendingGstConnection {
  gstin: string;
  username: string;
  fetchJobId?: string | null;
}

/** Pending connection + current animation phase. */
export interface PendingConnectionProgress extends PendingGstConnection {
  phase: ConnectionPhase;
  errorMessage?: string;
}

// ── Modal step type & title/subtitle defaults ───────────────────────────────

/** Steps of the GST connection modal flow. Shared by all consumers. */
export type ModalStep = "form" | "otp" | "api_setup";

export const GST_MODAL_TITLES: Record<ModalStep, string> = {
  form: "Connect your GST account",
  otp: "Verify Your GST Account",
  api_setup: "Enable API access on GST Portal",
};

export const GST_MODAL_SUBTITLES: Record<ModalStep, string> = {
  form: "We'll fetch your GSTR-2B directly from the GST portal.",
  otp: "Enter the OTP received from GST Portal to connect the account.",
  api_setup: "Do this once for the GSTIN before requesting OTP in Merito.",
};

// ── Merito support escalation ───────────────────────────────────────────────

/** Merito support WhatsApp number (used for the "Contact Us" escalation). */
export const MERITO_WA_NUMBER = "919769686008";

/**
 * Open a WhatsApp chat with Merito support, pre-filled with the given message.
 * Reads the local user's name/company for context.
 */
export function openMeritoWa(message: string): void {
  const params = new URLSearchParams({ text: message });
  window.open(
    `https://wa.me/${MERITO_WA_NUMBER}?${params.toString()}`,
    "_blank",
    "noopener,noreferrer"
  );
}

/**
 * Build a WhatsApp support message pre-filled with the local user's
 * name and company. The `issue` suffix is consumer-specific.
 */
export function buildSupportMessage(issue: string): string {
  const user = getLocalUser();
  return `Hey. I'm ${user?.name ?? ""} from ${user?.companyName ?? ""}, ${issue}`;
}